import { ComponentProps, ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import logLogInScreenForgotPasswordTapped from '@alltrails/amplitude/events/logLogInScreenForgotPasswordTapped';
import Typography from '@alltrails/core/components/Typography';
import Heading from '@alltrails/denali/components/Heading';
import Link from '@alltrails/denali/components/Link';
import Logo from '@alltrails/denali/components/Logo';
import { ModalAriaLabelledByContext } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import LoginResponse from '../../types/LoginResponse';
import AltLoginsForm from '../AltLoginsForm';
import EmailPasswordLogin from '../EmailPasswordLogin';
import TermsOfService from '../TermsOfService';
import styles from './SignUpModal.module.scss';

// Desktop: https://app.zeplin.io/project/6218dc5177101a641929be50/screen/6218e13d32440b60dea65769
// Mobile: https://app.zeplin.io/project/6218dc5177101a641929be50/screen/6218dec5dccf0d54f6dee4f4

type LogInProps = Pick<ComponentProps<typeof AltLoginsForm>, 'appleLoginHandler' | 'facebookLoginHandler' | 'googleLoginHandler'> & {
  flatColors?: boolean;
  grantType?: string | null;
  heading?: ReactNode;
  handleLoginSuccess: ({ user }: LoginResponse) => void;
  prefillEmail?: string;
  signUpPrompt?: ReactNode;
};

const LogIn = ({ flatColors, grantType, heading, handleLoginSuccess, prefillEmail, signUpPrompt, ...loginHandlers }: LogInProps) => {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);
  const defaultHeading = prefillEmail ? (
    <FormattedMessage defaultMessage="Welcome back!" />
  ) : (
    <>
      <FormattedMessage defaultMessage="Welcome back." />
      <br />
      <FormattedMessage defaultMessage="Log in and start exploring." />
    </>
  );

  return (
    <div className={styles.content}>
      {prefillEmail && (
        <div className={styles.logo}>
          <Logo variant="symbol" size="lg" color="monochrome-dark" />
        </div>
      )}
      <Heading as="h4" id={ariaLabelledBy} size="400" className={styles.heading}>
        {heading || defaultHeading}
      </Heading>
      <div className={styles.login}>
        <div className={styles.form}>
          <EmailPasswordLogin grantType={grantType} handleLoginSuccess={handleLoginSuccess} prefillEmail={prefillEmail} />
        </div>
        <Typography component="div" variant="paragraph200">
          <Link
            href="/users/password/new"
            onClick={() => {
              logLogInScreenForgotPasswordTapped();
            }}
          >
            <FormattedMessage defaultMessage="Forgot your password?" />
          </Link>
        </Typography>
        {!prefillEmail && <AltLoginsForm {...loginHandlers} authType="login" flatColors={flatColors} />}
        {signUpPrompt}
        <TermsOfService />
      </div>
    </div>
  );
};

export default LogIn;
